import React, { useEffect, useRef, useState } from 'react';
import { find } from 'lodash';
import { createPortal } from 'react-dom';

interface Props {
  answer: any;
  indexInGroup: number;
  prompt: any;
}

const StringInlineInput: React.FC<Props> = (props) => {
  const prompt = props.prompt;
  const answerPart = props.answer && props.answer.parts[0];

  const [inlineInput, setInlineInput] = useState(null);
  const [inlineText, setInlineText] = useState('');
  const [value, setValue] = useState(answerPart?.text || '');

  const wordWithoutBlanks = inlineText.replace(/_/g, '');
  const blankCount = inlineText.length - wordWithoutBlanks.length

  useEffect(() => {
    if (inlineInput) return;

    const targetInput = document.getElementsByClassName('input-inline')[props.indexInGroup] as HTMLElement;
    if (!targetInput) return;

    setInlineText(targetInput.innerText);
    targetInput.innerText = null;
    setInlineInput(targetInput);
  }, []);

  const handleChange = (newValue) => {
    setValue(newValue);

    if (newValue.length === blankCount) {
      const nextInlineInput = document.getElementsByClassName('input-inline')[props.indexInGroup + 1] as HTMLElement;

      if (nextInlineInput && nextInlineInput.getElementsByTagName('input')[0]) {
        nextInlineInput.getElementsByTagName('input')[0].focus();
      } else {
        (document.activeElement as HTMLElement).blur();
      }
    }
  };

  return (
    <div className="answer_string">
      <div className="answer_input answer-inputs">
        <input
          name={`prompts[${prompt.id}][${prompt.answerChoiceGroups[0].id}][text][]`}
          type="hidden"
          value={value}
        />
      </div>

      {
        inlineInput && createPortal(
          props.answer.isGraded ? (
            <GradedInput
              answer={props.answer}
              blankCount={blankCount}
              correctValue={find(prompt.answerChoiceGroups[0].answerChoices, (choice) => choice.isCorrect).text}
              wordWithoutBlanks={wordWithoutBlanks}
            />
          ) : (
            <Input
              blankCount={blankCount}
              onChange={handleChange}
              value={value}
              wordWithoutBlanks={wordWithoutBlanks}
            />
          ),
          inlineInput
        )
      }
    </div>
  );
};

interface InputProps {
  blankCount: number;
  onChange: (newValue: string) => void;
  value: string;
  wordWithoutBlanks: string;
}

const Input: React.FC<InputProps> = (props) => {
  const inputRef = useRef(null);
  const [inputting, setInputting] = useState(false);

  useEffect(() => {
    if (!inputting) return;

    inputRef.current.focus();
    inputRef.current.setSelectionRange(0, 0);
  // TODO - move cursor to beginning
  }, [inputting]);

  return (
    <>
      {props.wordWithoutBlanks}

      {
        (inputting || props.value.length !== props.blankCount) ? (
          <input
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            maxLength={props.blankCount}
            onBlur={() => setInputting(false)}
            onFocus={() => setInputting(true)}
            onChange={(e) => props.onChange(e.target.value)}
            ref={inputRef}
            size={props.blankCount}
            spellCheck={false}
            style={{
              background: 'repeating-linear-gradient(90deg, dimgrey 0, dimgrey 1ch, transparent 0, transparent 1.3ch) 0 100%/100% 1px no-repeat',
              backgroundColor: 'lightgrey',
              border: 'none',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              letterSpacing: '.2ch',
              outline: 'none',
              padding: 0
            }}
            type="text"
            value={props.value}
          />
        ) : (
          <span
            onClick={() => setInputting(true)}
            onFocus={() => setInputting(true)}
            style={{ backgroundColor: 'lightgrey', fontWeight: 'bold' }}
            tabIndex={0}
          >
            {props.value}
          </span>
        )
      }
    </>
  )
};

interface GradedInputProps {
  answer: any;
  blankCount: number;
  correctValue: string;
  wordWithoutBlanks: string;
}

const GradedInput: React.FC<GradedInputProps> = (props) => {
  const answerValue = props.answer.parts[0]?.text;
  const isCorrect = props.answer.isCorrect

  return (
    <>
      {props.wordWithoutBlanks}

      <>
        {
          !isCorrect && (
            <span style={{ backgroundColor: '#9bdcae', fontWeight: 'bold' }}>
              {props.correctValue}
            </span>
          )
        }

        <span
          style={{
            backgroundColor: isCorrect ? '#9bdcae' : '#ecacae',
            color: isCorrect ? 'inherit' : '#D9595D',
            fontWeight: 'bold',
            marginLeft: isCorrect ? 0 : 2,
            textDecoration: isCorrect ? 'none' : 'line-through'
          }}
        >
          {answerValue || <span dangerouslySetInnerHTML={{ __html: '&nbsp;&nbsp;&nbsp;&nbsp;' }} />}
        </span>
      </>
    </>
  )
};

export default StringInlineInput;
